import { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Play, Pause, Trash2, Download, Sparkles, Loader2 } from 'lucide-react';
import { C } from '../styles/tokens';
import { AudioInput } from '../components/audio-input';
import useIsMobile from '../hooks/useIsMobile';
import {
    useVoiceIsolations,
    useCreateVoiceIsolation,
    useDeleteVoiceIsolation,
    fetchIsolationPresignedUrl,
    fetchIsolationDownloadUrl,
    uploadToS3,
} from '../http-hooks/ai-voices/useVoiceIsolation';
import { downloadFile } from '../utils/downloadFile';
import { toast } from 'react-toastify';
import GenerationsTable from '../components/media/GenerationsTable';
import ConfirmDialog from '../components/media/ConfirmDialog';
import FeatureCreditCost from '../components/plans/FeatureCreditCost';
import { useFeatureCreditGate } from '../http-hooks/organisation/useFeatureCreditEstimate';
import { AUDIO_EXTENSIONS, getExtension, stripExtension, formatDuration } from '../utils/audioFile';
import { addMixPanelEvent } from '../store/actions';

// ── Generations row ────────────────────────────────────────────────────────────
function IsolationRow({ item, org_id, integration_id, playingId, setPlayingId, gridTemplateColumns, isMobile }) {
    const [downloading, setDownloading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [delHover, setDelHover] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null); // cached signed URL, fetched lazily on first play
    const deleteMutation = useDeleteVoiceIsolation();
    const isReady = item.status === 'ready';
    const isFailed = item.status === 'failed';

    // Only one row plays at a time — when another row becomes the active one, stop ours.
    useEffect(() => {
        if (playingId !== item.id && isPlaying) {
            audioRef.current?.pause();
        }
    }, [playingId, item.id, isPlaying]);

    // Both play and download need the same signed vocals URL; fetch once and reuse.
    const resolveVocalsUrl = async () => {
        if (audioUrlRef.current) return audioUrlRef.current;
        const { url } = await fetchIsolationDownloadUrl(org_id, integration_id, item.id, 'vocals');
        audioUrlRef.current = url;
        return url;
    };

    const togglePlay = async () => {
        if (!isReady || loadingAudio) return;
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            setPlayingId(null);
            return;
        }

        try {
            setLoadingAudio(true);
            const url = await resolveVocalsUrl();
            if (audio.src !== url) audio.src = url;
            await audio.play();
            setIsPlaying(true);
            setPlayingId(item.id); // claims playback — every other row stops
        } catch (e) {
            console.error('Playback failed', e);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleDownload = async () => {
        if (!isReady || downloading) return;
        setDownloading(true);
        try {
            const url = await resolveVocalsUrl();
            await downloadFile(url, `${item.name}-vocals.${item.output_format || 'wav'}`);
        } catch (e) {
            console.error('Download failed', e);
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (deleteMutation.isLoading) return;
        setConfirmOpen(false);
        try {
            if (isPlaying) { audioRef.current?.pause(); setPlayingId(null); }
            await deleteMutation.mutateAsync({ org_id, integration_id, isolation_id: item.id });
            toast.success('Isolation deleted');
        } catch (e) {
            console.error('Delete failed', e);
            toast.error('Failed to delete. Please try again.');
        }
    };

    const playButton = (
        <button
            type="button"
            onClick={togglePlay}
            disabled={!isReady || loadingAudio}
            title={isReady ? (isPlaying ? 'Pause' : 'Play') : undefined}
            style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0, border: 'none', padding: 0,
                background: C.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isReady && !loadingAudio ? 'pointer' : 'default',
            }}
        >
            {item.status === 'processing' || loadingAudio
                ? <Loader2 size={15} color={C.n500} className="vi-spin" />
                : isFailed
                    ? <Sparkles size={15} color="#EF4444" />
                    : isPlaying
                        ? <Pause size={14} color={C.n700} fill={C.n700} />
                        : <Play size={14} color={C.n700} fill={C.n700} style={{ marginLeft: 1 }} />}
        </button>
    );

    const statusNode = item.status === 'processing' ? 'Processing…' : isFailed ? <span style={{ color: '#EF4444' }}>Failed</span> : formatDuration(item.audio_duration);

    const downloadButton = (
        <button
            type="button"
            disabled={!isReady || downloading}
            onClick={handleDownload}
            title="Download isolated vocals"
            style={{
                width: 26, height: 26, border: 'none', background: 'transparent', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isReady && !downloading ? 'pointer' : 'default',
            }}
        >
            {downloading
                ? <Loader2 size={15} color={C.n400} className="vi-spin" />
                : <Download size={15} color={C.n400} />}
        </button>
    );

    const deleteButton = (
        <button
            type="button"
            disabled={deleteMutation.isLoading}
            onClick={() => setConfirmOpen(true)}
            onMouseEnter={() => setDelHover(true)}
            onMouseLeave={() => setDelHover(false)}
            title="Delete isolation"
            style={{
                width: 26, height: 26, border: 'none', borderRadius: 7, flexShrink: 0,
                background: delHover ? '#FEE2E2' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: deleteMutation.isLoading ? 'default' : 'pointer',
            }}
        >
            {deleteMutation.isLoading
                ? <Loader2 size={15} color={C.n400} className="vi-spin" />
                : <Trash2 size={15} color={delHover ? '#DC2626' : C.n400} />}
        </button>
    );

    const confirmDialog = (
        <ConfirmDialog
            open={confirmOpen}
            title="Delete isolation"
            message={`Delete "${item.name}"? This can't be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setConfirmOpen(false)}
        />
    );

    const audioEl = <audio ref={audioRef} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} />;

    if (isMobile) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                padding: '12px 16px', borderTop: `1px solid ${C.n100}`,
                opacity: isReady ? 1 : 0.55,
            }}>
                {audioEl}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {playButton}
                    <span style={{
                        minWidth: 0, flex: 1, fontSize: 13.5, fontWeight: 600, color: isReady ? C.n900 : C.n400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {item.name}
                    </span>
                    <span style={{ fontSize: 12.5, color: C.n500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {statusNode}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        {downloadButton}
                        {deleteButton}
                    </div>
                </div>
                {confirmDialog}
            </div>
        );
    }

    return (
        <div style={{
            display: 'grid', gridTemplateColumns, gap: 12, alignItems: 'center',
            padding: '12px 20px', borderTop: `1px solid ${C.n100}`,
            opacity: isReady ? 1 : 0.55,
        }}>
            {audioEl}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {playButton}
                <span style={{
                    minWidth: 0, fontSize: 13.5, fontWeight: 600, color: isReady ? C.n900 : C.n400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {item.name}
                </span>
            </div>
            <div style={{ fontSize: 12.5, color: C.n500, textAlign: 'center' }}>
                {statusNode}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                {downloadButton}
                {deleteButton}
            </div>
            {confirmDialog}
        </div>
    );
}

function IsolatorGenerations({ org_id, integration_id }) {
    const [playingId, setPlayingId] = useState(null);
    const { data = [], isLoading } = useVoiceIsolations({ org_id, integration_id });

    return (
        <GenerationsTable
            title="Generations"
            count={data.length}
            columns={[
                { key: 'duration', label: 'Duration', width: '110px' },
            ]}
            items={data}
            rowKey={item => item.id}
            renderRow={(item, gridTemplateColumns, key, isMobile) => (
                <IsolationRow
                    key={key}
                    item={item}
                    org_id={org_id}
                    integration_id={integration_id}
                    playingId={playingId}
                    setPlayingId={setPlayingId}
                    gridTemplateColumns={gridTemplateColumns}
                    isMobile={isMobile}
                />
            )}
            loading={isLoading}
            emptyIcon={<Sparkles size={24} color={C.n300} />}
            emptyTitle="No isolations yet"
            emptySubtitle="Upload or record audio to pull out a clean, studio-grade voice"
        />
    );
}

export default function VoiceIsolator() {
    const { org_id, id: integration_id } = useParams();
    const isMobile = useIsMobile();

    const [audioValue, setAudioValue] = useState(null);
    const [error, setError] = useState(null);
    const createIsolation = useCreateVoiceIsolation();
    const [submitting, setSubmitting] = useState(false);
    const dispatch = useDispatch();

    const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
        org_id, feature: 'voice-isolation',
        duration_seconds: audioValue?.durationSec != null ? Math.round(audioValue.durationSec) : null,
        enabled: !!audioValue,
    });

    const handleIsolate = async () => {
        if (!audioValue || submitting) return;
        const file = audioValue.file;
        setError(null);
        setSubmitting(true);
        try {
            const ext = getExtension(file.name) || 'wav';
            const contentType = file.type && file.type.startsWith('audio/') ? file.type : 'audio/mpeg';

            const { signed_url, url } = await fetchIsolationPresignedUrl({
                org_id, integration_id, fileName: file.name, contentType,
            });
            await uploadToS3(signed_url, file);

            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: integration_id, body: {
                    eventName: "VOICE_ISOLATOR_STARTED",
                    eventData: {
                        tool: "voice_isolator", // GROW-84: tool on every stage for start->finish comparison
                        source_audio_path: url,
                        source_format: ext,
                        use_tta: true,
                        audio_duration: audioValue.durationSec != null ? Math.round(audioValue.durationSec) : undefined,
                    }
                }
            }));

            await createIsolation.mutateAsync({
                org_id, integration_id,
                name: stripExtension(file.name),
                source_audio_path: url,
                source_format: ext,
                audio_duration: audioValue.durationSec != null ? Math.round(audioValue.durationSec) : undefined,
                use_tta: true,
            });

            setAudioValue(null);
        } catch (e) {
            setError(e?.message || 'Failed to start isolation. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-.02em', color: C.n900, margin: '0 0 8px' }}>
                Voice Isolator
            </h1>
            <p style={{ fontSize: 13.5, color: C.n500, margin: '0 0 24px' }}>
                Strip background noise and pull a clean, studio-grade voice out of any recording.
            </p>

            <AudioInput
                compact
                value={audioValue}
                onChange={setAudioValue}
                onError={e => setError(e.message)}
                renderError={() => null}
                validation={{ maxSizeBytes: 50 * 1024 * 1024, maxDurationSec: 600, allowedExtensions: AUDIO_EXTENSIONS }}
                recording={{ micDeviceSelector: true, maxRecordingDurationSec: 600 }}
            />

            {error && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#EF4444', fontWeight: 500 }}>{error}</div>
            )}

            {audioValue && (
                <div className="mt-3 d-flex justify-content-end align-items-center" style={{ gap: 12 }}>
                    <FeatureCreditCost
                        orgId={org_id}
                        feature="voice-isolation"
                        durationSeconds={audioValue.durationSec != null ? Math.round(audioValue.durationSec) : null}
                    />
                    <button
                        type="button"
                        disabled={submitting || hasInsufficientCredits}
                        onClick={handleIsolate}
                        title={hasInsufficientCredits ? 'Not enough credits to isolate this voice' : ''}
                        style={{
                            padding: '10px 18px', borderRadius: 10, border: 'none', flexShrink: 0,
                            background: submitting || hasInsufficientCredits ? C.n200 : C.n900,
                            color: submitting || hasInsufficientCredits ? C.n400 : C.w,
                            fontSize: 13.5, fontWeight: 700, cursor: submitting || hasInsufficientCredits ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
                        }}
                    >
                        {submitting && <Loader2 size={14} className="vi-spin" />}
                        {submitting ? 'Isolating…' : 'Isolate voice'}
                    </button>
                </div>
            )}

            <IsolatorGenerations org_id={org_id} integration_id={integration_id} />
        </div>
    );
}
